import { apiService } from "@/_services";
import { AnalyticsManager } from "./AnalyticsManager";
import {
  dateRangeToString,
  formatDate_YMD,
  longShortTransform,
  sortByKey,
  timeParseNoNull2,
  timeParseNull2,
  todayWithoutTime,
} from "@/_core/_helpers/_helpers";
import { timeDay, timeParse, nest, sum } from "d3";
import {
  longShort,
  derivativeImage,
} from "@/_core/charts/D3ChartAnalyticsChartHelpers";
import { AnalyticsHelpers } from "./AnalyticsHelpers";
import { formatPercentSm } from "@/_core/_helpers/_parametric_tools";
import { AnalyticsScenarios } from "./AnalyticsScenarios";
import { add, nonNans } from "@/_core/data/Advanced";

export class Analytics {
  constructor({
    analyticsPreferences,
    assetType,
    companies,
    companyParameters,
    images,
    processData,
    underlyingSpecs,
    customFilters,
    isInitial = true,
  }) {
    this.analyticsPreferences = analyticsPreferences;
    this.processData = processData;
    this.analyticsManager = new AnalyticsManager(this.processData, assetType);
    this.customFilters = customFilters;
    this.assetType = assetType;
    this.companies = companies;
    this.companyParameters = companyParameters;
    this.images = images;
    this.isInitial = isInitial;
    this.loading = {
      accounting: false,
      budget: false,
      expo: false,
      expoFuture: false,
      expoPast: false,
      hedges: false,
      hedgesFuture: false,
      hedgesPast: false,
      histCandles: false,
      management: false,
      pastCalcs: false,
      portCalcs: false,
      program: false,
      spot: false,
      strategies: false,
      derivatives: false,
      topographyCalcs: false,
      topographyCalcsRates: false,
      valuationFinalSpot: false,
      valuationInitialSpot: false,
      showModalLoading: false,
      messageModal: "",
      optionsModal: false,
    };
    this.managementFields = [
      {
        id: 0,
        dataType: "",
        field: "contract_month_year",
        header: "Contract",
        styles: { "max-width": "5rem" },
      },
    ];
    this.rawData = {
      budget: [],
      dataFilters: this.analyticsManager.defineFilters(this.customFilters),
      dDowns: this.analyticsManager.defineDDowns(),
      derivatives: [],
      existLCall: false,
      existLFwd: false,
      existLPut: false,
      existSCall: false,
      existSFwd: false,
      existSPut: false,
      expo: [],
      expoFuture: [],
      expoPast: [],
      filteredData: [],
      hedges: [],
      hedgesFuture: [],
      hedgesPast: [],
      historicalData: [],
      historicalOHLC: {},
      mergedExpoHedges: [],
      nestedData: [],
      prevSpot: null,
      program: [],
      render: true,
      spot: null,
      strategies: [],
      toBeFetched: true,
      toBeFetchedHidden: false,
      totals: this.analyticsManager.defineTotals(),
      years: {},
      zoomedData: [],
      zoomedDerivatives: [],
    };
    this.optionTypes = [];
    this.underlyingSpecs = underlyingSpecs;
    this.underlyingObject = {};
    for (let i = 0; i < underlyingSpecs.length; i++) {
      this.underlyingObject[underlyingSpecs[i].pk_idcommodity] =
        underlyingSpecs[i];
    }
    this.userSelections = {
      assets: [
        {
          product_name: "N.A.",
          fk_idcurrency: "N.A.",
          fk_idunit: "N.A.",
          currency_name: "N.A/N.A",
        },
      ],
      assetsNums: [],
      bucket: "MONTHLY",
      expandSidebar: false,
      fldBudget: "p_budget",
      fldBasis: "p_commited",
      fldFixed: "p_fixedHedged",
      fldFixedPerc: "p_fixedHedgedPerc",
      fldMkt: "p_market",
      fldMktPerc: "p_marketPerc",
      fldNet: "p_net",
      fldNetPerc: "p_netPerc",
      includeFutures: true,
      includeBasis: true,
      includeRatio: true,
      includeOtherCosts: true,
      intervalGroup: "1M",
      isCommodity: this.assetType === "commodity",
      mktRef: "spot",
      normalizeQty: false,
      portfolioNums: [],
      portfolios: [],
      selectedAnalysis: "scenarios",
      selectedAnalysisParent: "scenario",
      selectedAsset: [
        {
          product_name: "N.A.",
          fk_idcurrency: "N.A.",
          fk_idunit: "N.A.",
          currency_name: "N.A/N.A",
        },
      ],
      selectedCompanies: [],
      selectedCurrencies: [],
      selectedPortfolios: [],
      selectedTable: "strategies",
      selectedUnderlying: [{ commodity_name: "N.A." }],
      selectedUnit: "contracts",
      selectionStr: "",
      showAll: false,
      showAve: false,
      showBudget: true,
      showCommited: true,
      showForecast: true,
      showDetails: true,
      showFixedBasis: true,
      showFixedFutures: true,
      showHedgedFutures: false,
      showFixedHedged: false,
      showLCall: false,
      showLFwd: false,
      showLPut: false,
      showMarket: true,
      showNet: true,
      showProgram: true,
      showSCall: false,
      showSFwd: false,
      showSimulations: false,
      showSPut: false,
      underlying: [{ commodity_name: "N.A." }],
      underlyingNums: [],
      visibleLeft: false,
      xZoom: [],
      year: null,
    };
    this.analyticsHelpers = new AnalyticsHelpers(
      this.processData,
      this.rawData,
      this.userSelections,
    );
    this.analyticsScenarios = new AnalyticsScenarios(
      this.processData,
      this.rawData,
      this.userSelections,
    );
    this.management = {
      strategies: [],
      contracts: [],
      date: [],
      clients: [],
      suppliers: [],
    };
    this.valuation = {
      data: this.analyticsManager.defineEmptyValTable(),
      dataFilters: this.analyticsManager.defineFilters(this.customFilters),
      dDowns: this.analyticsManager.defineDDowns(),
      finalDate: null,
      finalSpot: null,
      initialDate: null,
      initialSpot: null,
      toBeFetched: true,
      valuated: false,
    };
  }

  getDataRaw({ nameData, endpoint, extraParams = {}, dateFields = {} }) {
    Object.assign(this.rawData, {
      [nameData]: [],
    });
    const assets = this.userSelections.isCommodity
      ? "end_product"
      : "currencies";

    const processDataRaw = endpoint.includes("hedge")
      ? "processDataRawHedge"
      : "processDataRawExpo";

    const params = {
      method: "GET",
      endpoint: endpoint,
      content_type: "application/json",
      query_params: {
        format: "json",
        [assets]: {
          id_assets: this.userSelections["assetsNums"],
        },
        portfolios: { id_portfolios: this.userSelections["portfolioNums"] },
        date: formatDate_YMD(new Date()),
        ...extraParams,
      },
    };
    apiService
      .getAll(params)
      .then((data) => {
        Object.assign(this.rawData, {
          [nameData]: this[processDataRaw](data, dateFields),
        });
        Object.assign(this.loading, { [nameData]: false });
      })
      .catch((error) => {
        Object.assign(this.loading, { [nameData]: false });
        console.error(error);
      });
  }

  /** Process Raw Expo data for selected company/portfolio/asset */
  processDataRawExpo(data, dateFields) {
    data = JSON.parse(JSON.stringify(data));
    data.forEach((d) => {
      for (let i = 0; i < dateFields.length; i++) {
        d[dateFields[i]] = timeParseNull2(d[dateFields[i]]);
      }
      if (d.planned_expiration_date != null) {
        d.date = d.planned_expiration_date;
      }
      d.futures = d[this.assetType];
      if (!this.userSelections.isCommodity) {
        d.unit = d.currency.slice(0, 3);
        d.curr = d.currency.slice(3);
      }
    });
    return data;
  }

  /** Process Raw Hedge data for selected company/portfolio/asset */
  processDataRawHedge(data, dateFields) {
    data = JSON.parse(JSON.stringify(data));
    data.forEach((d) => {
      for (let i = 0; i < dateFields.length; i++) {
        d[dateFields[i]] = timeParseNull2(d[dateFields[i]]);
      }
      d.date = d.fixing_date;
      d.notional = +d[
        this.userSelections.isCommodity ? "quantity" : "notional"
      ];
      d.spot = d.spot != null ? +d.spot : null;
      d.hedgeNotional = +d.hedgeNotional;
      d.strike = d.strike != null ? +d.strike : null;
      d.fwd = d.fwd != null ? +d.fwd : null;
      d.derivative_name = `${longShort(d.buy_sell)} ${d.derivative}`;
      d.strategy_name = d.strategy_name.toLowerCase();
      d.strategy_name = d.strategy_name.includes("unwind")
        ? d.strategy_name
            .replace(/(\w+)\s+unwind\s+(\w+)/, "unwind $1 $2")
            .replace("unwind", "offset")
        : d.strategy_name;
      d.cash_flow = 0;
      d.balance = 0;
      if (!this.userSelections.isCommodity) {
        d.unit = d.currency.slice(0, 3);
        d.curr = d.currency.slice(3);
      }
    });
    return data;
  }

  /** Process Raw Expo data for selected company/portfolio/asset */
  processMergedData(data, dateFields) {
    data.forEach((d) => {
      for (let i = 0; i < dateFields.length; i++) {
        d[dateFields[i]] = timeParseNull2(d[dateFields[i]]);
      }
      d.notional = +d[
        this.userSelections.isCommodity ? "quantity" : "notional"
      ];
      d.notional_hedge = +d.notional_hedge;
      d.spot = d.spot != null ? +d.spot : null;
      d.hedgeNotional = +d.hedgeNotional;
      d.strike = d.strike != null ? +d.strike : null;
      d.fwd = d.fwd != null ? +d.fwd : null;
      d.derivative_name = d.derivative
        ? `${longShort(d.buy_sell)} ${d.derivative}`
        : null;
      this.analyticsHelpers.existDerivatives(d);
    });
    return data;
  }

  /** Retrieves user rawData for selected company/portfolio/asset */
  getAllRawData({ endPoints = [] }) {
    // loadingExpoHedges when finished
    Object.assign(this.loading, {
      budget: true,
      expoFuture: true,
      expoPast: true,
      hedgesFuture: true,
      hedgesPast: true,
      pastCalcs: true,
      portCalcs: true,
      program: true,
      strategies: true,
      derivatives: true,
      topographyCalcs: true,
      topographyCalcsRates: true,
    });

    this.resetData();
    endPoints.forEach((endpoint) => {
      this.getDataRaw({ ...endpoint });
    });

    Object.assign(this.rawData, { toBeFetched: false });
    Object.assign(this.rawData, { toBeFetchedHidden: true });
  }

  /** Process Raw Program data for selected company/portfolio/asset */
  //getCmdtyRawProgram
  getRawProgram(endpoint) {
    this["rawData"]["program"] = [];

    const { assets, selectedAsset, portfolioNums } = this.userSelections;
    const asset = this.userSelections.isCommodity
      ? "pk_id_end_product"
      : "pk_i_idcurrency";

    let params = {
      method: "GET",
      endpoint: endpoint,
      content_type: "application/json",
      query_params: {
        format: "json",
        [this.assetType]: parseInt(assets[selectedAsset][asset]),
        portfolios: { id_portfolios: portfolioNums },
      },
    };
    apiService
      .getAll(params)
      .then((data) => {
        data.forEach((d) => {
          d.flow = JSON.parse(d.flow);
        });
        this["rawData"]["program"] = data;
        this["loading"]["program"] = false;
      })
      .catch((error) => {
        this["loading"]["program"] = false;
        console.error(error);
      });
  }

  averageIgnoringNaN(arr) {
    const filteredArr = arr.filter((num) => !isNaN(num)); // Remove NaN values
    if (filteredArr.length === 0) return NaN; // Handle case where all values are NaN

    const sum = filteredArr.reduce((acc, num) => acc + num, 0);
    return sum / filteredArr.length;
  }

  /** Organize and calculate data grouped by time intervals. */
  // nestCommoditiesFct
  nestDataFct(data, intervalGroup) {
    data = sortByKey(data, "date");
    let sign = Math.sign(sum(data, (d) => d.cash_flow));
    sign = sign === 0 ? Math.sign(sum(data, (d) => d.balance)) : sign;
    sign = sign === 0 ? 1 : sign;

    // exportToCSV(data, 'data_to_nest.csv')

    return nest()
      .key((d) =>
        this.analyticsHelpers.timeComparison(d.date, intervalGroup, data),
      )
      .rollup((v) => this.nestingMainCalcs(v, intervalGroup, data, sign))
      .entries(data)
      .map((d) => d.value);
  }

  /** filterHedges for derivatives chart filtering */
  filterHedges() {
    const { zoomedData, expoPast, expoFuture, expo } = this.rawData;
    const len = zoomedData.length - 1;

    if (len < 0) {
      return;
    }
    let lastDate = zoomedData[len].date;
    let xMax = timeDay(
      timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(JSON.parse(JSON.stringify(lastDate))),
    );

    const intGr = this.userSelections["intervalGroup"];
    const intervalIncrements = {
      "1D": 1,
      "1W": 7,
      "2W": 15,
      "1M": 1,
      "1Q": 3,
      "6M": 6,
      "1Y": 1,
    };

    const increment = intervalIncrements[intGr] || 0;
    if (increment > 0) {
      switch (true) {
        case intGr.includes("D") || intGr.includes("W"):
          xMax.setDate(xMax.getDate() + increment);
          break;
        case intGr.includes("M") || intGr.includes("Q"):
          xMax.setMonth(xMax.getMonth() + increment);
          break;
        default:
          xMax.setFullYear(xMax.getFullYear() + increment);
      }
    }

    let minDate = +zoomedData[0].date;
    let maxDate = +xMax;
    const expoData = expo.length > 0 ? expo : expoPast.concat(expoFuture);

    Object.assign(this.rawData, {
      zoomedDerivatives: this.rawData.filteredData.filter(
        (d) => +d.date >= minDate && +d.date < maxDate,
      ),
      filteredExpoData: expoData.filter(
        (d) => +d.date >= minDate && +d.date < maxDate,
      ),
    });

    // === Data derivatives
    this.analyticsScenarios.getDataNestAnalysisScenarios({
      _this: this,
      intervalGroup: intGr,
      nameData: "derivatives",
      analysisFieldName: "derivative_name",
      valueQty: "notional",
      calculateDelta: true,
      filterData: "zoomedDerivatives",
    });
    // === Data strategies
    if (this.management.date.length > 0) {
      this.analyticsScenarios.getNestedAnalysisDataWithCustomDates({
        _this: this,
        intervalGroup: intGr,
        nameData: "strategies",
        analysisFieldName: "strategy_name",
        valueQty: "q_current",
        filterData: this.management.date.filter(
          (d) => +d.monthDate >= minDate && +d.monthDate < maxDate,
        ),
        calculateMinMax: true,
        calculateBalanceQty: true,
        isCustomDelta: true,
        keyStrike: "strategy_strike",
        fieldDate: "monthDate",
        isAverageTotal: true,
        isQtyTotals: true,
        isAbs: false,
        isRefPrice: true,
        keyTotalIncludes: [
          { name: "totals", isPrice: false },
          { name: "average", isPrice: true },
          { name: "min", isPrice: false },
          { name: "max", isPrice: false },
          { name: "delta", isPrice: false },
        ],
      });
    } else {
      Object.assign(this.loading, { strategies: false });
    }
    // === Data providers
    if (this.management.suppliers.length > 0) {
      this.analyticsScenarios.getDataNestAnalysisScenarios({
        _this: this,
        intervalGroup: intGr,
        nameData: "providers",
        analysisFieldName: "supplier",
        valueQty: "q_current",
        calculateStrike: false,
        filterData: this.management.suppliers
          .filter((d) => !d.total)
          .filter((d) => +d.monthDate >= minDate && +d.monthDate < maxDate),
        fieldDate: "monthDate",
        ignore_field: "exposure_type",
        ignore_values: ["BUDGET", "PROJECTED"],
      });
    } else {
      Object.assign(this.loading, { providers: false });
    }
    // === Data clients
    if (this.management.clients.length > 0) {
      this.analyticsScenarios.getDataNestAnalysisScenarios({
        _this: this,
        intervalGroup: intGr,
        nameData: "clients",
        analysisFieldName: "client_str",
        valueQty: "q_current",
        calculateStrike: false,
        filterData: this.management.clients
          .filter((d) => !d.total)
          .filter((d) => +d.monthDate >= minDate && +d.monthDate < maxDate),
        fieldDate: "monthDate",
        ignore_field: "exposure_type",
        ignore_values: ["BUDGET", "PROJECTED"],
      });
    } else {
      Object.assign(this.loading, { clients: false });
    }
    // === Data counterparty
    this.analyticsScenarios.getDataNestAnalysisScenarios({
      _this: this,
      intervalGroup: intGr,
      nameData: "counterparty",
      analysisFieldName: "counterparty",
      valueQty: "q_current",
      calculateStrike: false,
      filterData: "filteredExpoData",
      ignore_field: "exposure_type",
      ignore_values: ["BUDGET", "PROJECTED"],
    });
  }

  /** Nest and zoom after filtering */
  nestAndZoom(data, calculate = true) {
    Object.assign(this.rawData, {
      nestedData: this.nestAll(data),
    });
    if (calculate) {
      this.analyticsHelpers.getYears();
    }
    Object.assign(this.rawData, {
      zoomedData: this.zoomData(
        this.rawData["nestedData"],
        this.userSelections["xZoom"],
      ),
    });
    this.filterHedges();
    Object.assign(this.rawData, {
      totals: [this.zoomedTotals()],
    });
    this.updateZoomedData();
    this.updateZoomedData(true);
    Object.assign(this.loading, { topographyCalcs: false });
    let bool = !this.rawData["render"];
    Object.assign(this.rawData, { render: bool });
  }

  updateZoomedData(isTotals = false) {
    let zoomedData;
    if (!isTotals) {
      zoomedData = [...this.rawData["zoomedData"]];
    } else {
      zoomedData = [...this.rawData["totals"]];
    }
    const selections = this.userSelections;
    let _this = this;
    const timestamp = Date.now();
    zoomedData.forEach(function(d, index) {
      let dat = _this.createAnalyticsVars(
        d,
        selections.includeFutures,
        selections.includeRatio,
        selections.includeBasis,
        selections.includeOtherCosts,
      );
      d.id = `item-${timestamp}-${index}`;
      d["budget"] = dat["budget"];
      d["comm"] = dat["comm"];
      d["fixed"] = dat["fixed"];
      d["fixedHedged"] = dat["fixedHedged"];
      d["hedge"] = dat["hedge"];
      d["market"] = dat["market"];
      d["net"] = dat["net"];

      d["budgetPerc"] = dat["budgetPerc"];
      d["commPerc"] = dat["commPerc"];
      d["fixedPerc"] = dat["fixedPerc"];
      d["fixedHedgedPerc"] = dat["fixedHedgedPerc"];
      d["hedgePerc"] = dat["hedgePerc"];
      d["marketPerc"] = dat["marketPerc"];
      d["netPerc"] = dat["netPerc"];
    });
    if (!isTotals) {
      Object.assign(this.rawData, {
        zoomedData: zoomedData,
      });
    } else {
      Object.assign(this.rawData, {
        totals: zoomedData,
      });
    }
  }

  /** Calculates various normalized, weighted and formatted totals and percentages  */
  zoomedTotals() {
    // function aveFct(array) {
    //   return array.reduce((a, b) => a + b) / array.length;
    // }
    const data = this.rawData.zoomedData;
    const len = data.length - 1;
    let date = "date...";
    if (len > -1) {
      if (Object.keys(data[0]).indexOf("date") !== -1) {
        date = dateRangeToString(data[0].date, data[len].date, true, "\u2192");
      }
    }
    // QUANTITIES ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
    const q_forecast = data.map((d) => nonNans(d.q_forecast)).reduce(add, 0);
    const q_budget = data.map((d) => nonNans(d.q_budget)).reduce(add, 0);
    const q_commited = data.map((d) => nonNans(d.q_commited)).reduce(add, 0);
    const q_fixed = data.map((d) => nonNans(d.q_fixed)).reduce(add, 0);
    const q_program = data.map((d) => nonNans(d.q_program)).reduce(add, 0);
    const q_fixedFut = data.map((d) => nonNans(d.q_fixedFut)).reduce(add, 0);
    const q_fixedHedged = data
      .map((d) => nonNans(d.q_fixedHedged))
      .reduce(add, 0);
    const q_fixedHedgedFut = data
      .map((d) => nonNans(d.q_fixedHedgedFut))
      .reduce(add, 0);
    const q_hedged = q_fixedHedged - q_fixed;
    const q_hedgedFut = q_fixedHedgedFut - q_fixedFut;
    const v_netVsMktFut = data
      .map((d) => nonNans(d.v_netVsMktFut))
      .reduce(add, 0);
    const v_netVsMktBasis = data
      .map((d) => nonNans(d.v_netVsMktBasis))
      .reduce(add, 0);
    const v_netVsMktRatio = data
      .map((d) => nonNans(d.v_netVsMktRatio))
      .reduce(add, 0);
    // const v_hedge_ratio = data
    //   .map((d) => nonNans(d.v_hedge_ratio))
    //   .reduce(add, 0);
    const v_netVsMkt = data.map((d) => nonNans(d.v_netVsMkt)).reduce(add, 0);
    const v_fixedHedged = data
      .map((d) => nonNans(d.v_fixedHedged))
      .reduce(add, 0);
    const v_exposedRatio = data
      .map((d) => nonNans(d.v_exposedRatio))
      .reduce(add, 0);
    const v_exposedBasis = data
      .map((d) => nonNans(d.v_exposedBasis))
      .reduce(add, 0);
    const v_budget = data.map((d) => nonNans(d.v_budget)).reduce(add, 0);
    // const v_budget_hedge_ratio = data
    //   .map((d) => nonNans(d.v_budget_hedge_ratio))
    //   .reduce(add, 0);
    const v_budgetFixedHedgedFut = data
      .map((d) => nonNans(d.v_budgetFixedHedgedFut))
      .reduce(add, 0);
    const v_budgetBasis = data
      .map((d) => nonNans(d.v_budgetBasis))
      .reduce(add, 0);
    const v_budgetRatio = data
      .map((d) => nonNans(d.v_budgetRatio))
      .reduce(add, 0);

    // BUDGET ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
    const p_budgetBasis =
      data.map((d) => nonNans(d.budgetPOther * d.q_forecast)).reduce(add, 0) /
      q_forecast;
    const p_budgetRatio =
      data.map((d) => nonNans(d.p_budgetRatio * d.q_forecast)).reduce(add, 0) /
      q_forecast;
    const p_budgetFut =
      data
        .map((d) => nonNans(d.p_budgetFut * d.p_budgetRatio * d.q_forecast))
        .reduce(add, 0) /
      data.map((d) => nonNans(d.q_forecast * d.p_budgetRatio)).reduce(add, 0);

    const p_budget =
      data.map((d) => nonNans(d.p_budget * d.q_forecast)).reduce(add, 0) /
      q_forecast;
    const p_budgetOther =
      data.map((d) => nonNans(d.p_budgetOther * d.q_forecast)).reduce(add, 0) /
      q_forecast;

    // MARKET ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
    const p_marketBasis =
      data.map((d) => nonNans(d.marketPOther * d.q_forecast)).reduce(add, 0) /
      q_forecast;
    const p_marketRatio =
      data.map((d) => nonNans(d.p_marketRatio * d.q_forecast)).reduce(add, 0) /
      q_forecast;
    const p_marketOther =
      data.map((d) => nonNans(d.p_marketOther * d.q_forecast)).reduce(add, 0) /
      q_forecast;
    const p_marketFut =
      data
        .map((d) => nonNans(d.p_marketFut * d.p_marketRatio * d.q_forecast))
        .reduce(add, 0) /
      data.map((d) => nonNans(d.p_marketRatio * d.q_forecast)).reduce(add, 0);
    const p_market =
      data.map((d) => nonNans(d.p_market * d.q_forecast)).reduce(add, 0) /
      q_forecast;

    // Commited ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
    const p_commitedFut =
      data
        .map((d) =>
          nonNans(
            d.p_commitedFut *
              d.p_fixedHedgedRatio *
              Math.max(d.q_commited, d.q_forecast),
          ),
        )
        .reduce(add, 0) /
      data
        .map((d) =>
          nonNans(d.p_fixedHedgedRatio * Math.max(d.q_commited, d.q_forecast)),
        )
        .reduce(add, 0);

    const p_commited =
      data.map((d) => nonNans(d.p_commited * d.q_commited)).reduce(add, 0) /
      q_commited;
    const p_commitedBasis =
      data
        .map((d) => nonNans(d.p_commitedBasis * d.q_commited))
        .reduce(add, 0) / q_commited;
    const p_commitedOther =
      data
        .map((d) => nonNans(d.p_commitedOther * d.q_commited))
        .reduce(add, 0) / q_commited;
    const p_commitedRatio =
      data
        .map((d) => nonNans(d.p_commitedRatio * d.q_commited))
        .reduce(add, 0) / q_commited;

    // FIXED ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
    const p_fixedHedgedBasis =
      data.map((d) => nonNans(d.fixedPOther * d.q_commited)).reduce(add, 0) /
      q_commited;
    const p_fixedHedgedRatio =
      data
        .map((d) => nonNans(d.p_fixedHedgedRatio * d.q_commited))
        .reduce(add, 0) / q_commited;
    const p_fixedHedgedOther =
      data
        .map((d) => nonNans(d.p_fixedHedgedOther * d.q_commited))
        .reduce(add, 0) / q_commited;
    const p_fixedHedgedFut =
      data
        .map((d) =>
          nonNans(d.p_fixedHedgedFut * d.q_fixedHedged * d.p_fixedHedgedRatio),
        )
        .reduce(add, 0) /
      data
        .map((d) => nonNans(d.q_fixedHedged * d.p_fixedHedgedRatio))
        .reduce(add, 0);
    const p_fixedHedged =
      data
        .map((d) => nonNans(d.p_fixedHedged * d.q_fixedHedged))
        .reduce(add, 0) / q_fixedHedged;
    const p_fixed =
      data.map((d) => nonNans(d.p_fixed * d.q_fixed)).reduce(add, 0) / q_fixed;
    const p_hedged =
      data.map((d) => nonNans(d.p_hedged * d.q_hedged)).reduce(add, 0) /
      q_hedged;
    const p_fixedFut =
      data
        .map((d) => nonNans(d.p_fixedFut * d.q_fixed * d.p_fixedHedgedRatio))
        .reduce(add, 0) /
      data.map((d) => nonNans(d.q_fixed * d.p_fixedHedgedRatio)).reduce(add, 0);
    const p_hedgedFut =
      data
        .map((d) => nonNans(d.p_hedgedFut * d.q_hedged * d.p_fixedHedgedRatio))
        .reduce(add, 0) /
      data
        .map((d) => nonNans(d.q_hedged * d.p_fixedHedgedRatio))
        .reduce(add, 0);

    // NET ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
    const p_netBasis =
      data
        .map((d) => nonNans(d.netPOther * Math.max(d.q_commited, d.q_forecast)))
        .reduce(add, 0) /
      data
        .map((d) => nonNans(Math.max(d.q_commited, d.q_forecast)))
        .reduce(add, 0);
    const p_netRatio =
      data
        .map((d) =>
          nonNans(d.p_netRatio * Math.max(d.q_commited, d.q_forecast)),
        )
        .reduce(add, 0) /
      data
        .map((d) => nonNans(Math.max(d.q_commited, d.q_forecast)))
        .reduce(add, 0);
    const p_netOther =
      data
        .map((d) =>
          nonNans(d.p_netOther * Math.max(d.q_commited, d.q_forecast)),
        )
        .reduce(add, 0) /
      data
        .map((d) => nonNans(Math.max(d.q_commited, d.q_forecast)))
        .reduce(add, 0);
    const p_netFut =
      data
        .map((d) =>
          nonNans(
            d.p_netFut * d.p_netRatio * Math.max(d.q_commited, d.q_forecast),
          ),
        )
        .reduce(add, 0) /
      data
        .map((d) =>
          nonNans(d.p_netRatio * Math.max(d.q_commited, d.q_forecast)),
        )
        .reduce(add, 0);
    const p_net =
      data
        .map((d) => nonNans(d.p_net * Math.max(d.q_commited, d.q_forecast)))
        .reduce(add, 0) /
      data
        .map((d) => nonNans(Math.max(d.q_commited, d.q_forecast)))
        .reduce(add, 0);

    // CHANGES ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
    // ==== QUANTITIES ==== ==== ==== ==== ==== ==== ==== ====
    const q_budgetPerc = q_budget / q_forecast;
    const q_commitedPerc = q_commited / q_forecast;
    const q_commitedPercS = formatPercentSm(q_commited / q_forecast);
    const q_fixedPerc = q_fixed / q_forecast;
    const q_fixedPercS = formatPercentSm(q_fixed / q_forecast);
    const q_fixedHedgedPerc = q_fixedHedged / q_forecast;
    const q_hedgedPerc = q_hedged / q_forecast;
    const q_programPerc = q_program / q_forecast;
    // ==== BASIS ==== ==== ==== ==== ==== ==== ==== ====
    const p_fixedHedgedBasisPerc = p_fixedHedged / p_budget - 1;
    const p_marketBasisPerc = p_fixedHedged / p_budget - 1;
    const p_netBasisPerc = p_fixedHedged / p_budget - 1;
    // ==== RATIOS ==== ==== ==== ==== ==== ==== ==== ====
    const p_fixedHedgedRatioPerc = p_fixedHedgedRatio / p_budgetRatio - 1;
    const p_marketRatioPerc = p_marketRatio / p_budgetRatio - 1;
    const p_netRatioPerc = p_netRatio / p_budgetRatio - 1;
    // ==== FUTURES ==== ==== ==== ==== ==== ==== ==== ====
    const p_fixedHedgedFutPerc = p_fixedHedgedFut / p_budgetFut - 1;
    const p_marketFutPerc = p_marketFut / p_budgetFut - 1;
    const p_netFutPerc = p_netFut / p_budgetFut - 1;
    // ==== PRICES ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
    const m2m_fixedHedgedValue = data
      .map((d) => nonNans(d.m2m_fixedHedgedValue))
      .reduce(add, 0);
    const m2m_fixedValue = data
      .map((d) => nonNans(d.m2m_fixedValue))
      .reduce(add, 0);
    const m2m_hedgeValue = data
      .map((d) => nonNans(d.m2m_hedgeValue))
      .reduce(add, 0);
    const p_fixedHedgedPerc = p_fixedHedged / p_budget - 1;
    const p_fixedPerc = p_fixed / p_budget - 1;
    const p_hedgedPerc = p_hedged / p_budget - 1;
    const p_commitedPerc = p_commited / p_budget - 1;
    const p_marketPerc = p_market / p_budget - 1;
    const p_netPerc = p_net / p_budget - 1;

    return {
      date: date,
      m2m_fixedHedgedValue: m2m_fixedHedgedValue,
      m2m_fixedValue: m2m_fixedValue,
      m2m_hedgeValue: m2m_hedgeValue,
      p_budget: p_budget,
      p_budgetBasis: p_budgetBasis,
      p_budgetFut: p_budgetFut,
      p_budgetOther: p_budgetOther,
      p_budgetRatio: p_budgetRatio,
      p_commited: p_commited,
      p_commitedFut: p_commitedFut,
      p_commitedBasis: p_commitedBasis,
      p_commitedOther: p_commitedOther,
      p_commitedRatio: p_commitedRatio,
      p_commitedPerc: p_commitedPerc,
      p_fixed: p_fixed,
      p_fixedFut: p_fixedFut,
      p_fixedHedged: p_fixedHedged,
      p_fixedHedgedBasis: p_fixedHedgedBasis,
      p_fixedHedgedBasisPerc: p_fixedHedgedBasisPerc,
      p_fixedHedgedFut: p_fixedHedgedFut,
      p_fixedHedgedFutPerc: p_fixedHedgedFutPerc,
      p_fixedHedgedOther: p_fixedHedgedOther,
      p_fixedHedgedPerc: p_fixedHedgedPerc,
      p_fixedHedgedRatio: p_fixedHedgedRatio,
      p_fixedHedgedRatioPerc: p_fixedHedgedRatioPerc,
      p_fixedPerc: p_fixedPerc,
      p_hedged: p_hedged,
      p_hedgedFut: p_hedgedFut,
      p_hedgedPerc: p_hedgedPerc,
      p_market: p_market,
      p_marketBasis: p_marketBasis,
      p_marketBasisPerc: p_marketBasisPerc,
      p_marketFut: p_marketFut,
      p_marketFutPerc: p_marketFutPerc,
      p_marketOther: p_marketOther,
      p_marketPerc: p_marketPerc,
      p_marketRatio: p_marketRatio,
      p_marketRatioPerc: p_marketRatioPerc,
      p_net: p_net,
      p_netBasis: p_netBasis,
      p_netBasisPerc: p_netBasisPerc,
      p_netFut: p_netFut,
      p_netFutPerc: p_netFutPerc,
      p_netOther: p_netOther,
      p_netPerc: p_netPerc,
      p_netRatio: p_netRatio,
      p_netRatioPerc: p_netRatioPerc,
      q_budget: q_budget,
      q_budgetPerc: q_budgetPerc,
      q_commited: q_commited,
      q_commitedPerc: q_commitedPerc,
      q_commitedPercS: q_commitedPercS,
      q_fixed: q_fixed,
      q_fixedFut: q_fixedFut,
      q_fixedHedged: q_fixedHedged,
      q_fixedHedgedFut: q_fixedHedgedFut,
      q_fixedHedgedPerc: q_fixedHedgedPerc,
      q_fixedPerc: q_fixedPerc,
      q_fixedPercS: q_fixedPercS,
      q_forecast: q_forecast,
      q_hedged: q_hedged,
      q_hedgedFut: q_hedgedFut,
      q_hedgedPerc: q_hedgedPerc,
      q_program: q_program,
      q_programPerc: q_programPerc,
      v_budget: v_budget,
      v_budgetBasis: v_budgetBasis,
      v_budgetFixedHedgedFut: v_budgetFixedHedgedFut,
      v_budgetRatio: v_budgetRatio,
      v_exposedBasis: v_exposedBasis,
      v_exposedRatio: v_exposedRatio,
      v_fixedHedged: v_fixedHedged,
      v_netVsMkt: v_netVsMkt,
      v_netVsMktBasis: v_netVsMktBasis,
      v_netVsMktFut: v_netVsMktFut,
      v_netVsMktRatio: v_netVsMktRatio,
    };
  }

  /** Nest all expo and hedge data */
  nestAll(data) {
    const result = this.nestDataFct(data, this.userSelections["intervalGroup"]);
    Object.assign(this.rawData, { toBeFetched: false });
    return result;
  }

  zoomData(data, zoomVals) {
    return data.slice(zoomVals[0], zoomVals[1]);
  }

  /** Consolidate expo and hedges */
  consolidateExpoHedges(endpoint) {
    const { expoFuture, expoPast, hedgesFuture, hedgesPast } = this["rawData"];
    if (
      expoFuture.length === 0 &&
      expoPast.length === 0 &&
      hedgesFuture.length === 0 &&
      hedgesPast.length === 0
    ) {
      Object.assign(this.loading, { topographyCalcs: false });
      return;
    }
    Object.assign(this.loading, {
      topographyCalcs: true,
    });
    let params = {
      method: "POST",
      endpoint,
      content_type: "application/json",
      query_params: {
        companies: {
          id_companies: this.userSelections["selectedCompanies"].map(
            (d) => d.pk_idcompany,
          ),
        },
        format: "json",
        expoFuture: JSON.stringify(expoFuture),
        expoPast: JSON.stringify(expoPast),
        hedgesFuture: JSON.stringify(hedgesFuture),
        hedgesPast: JSON.stringify(hedgesPast),
      },
    };
    return apiService
      .getAll(params)
      .then((data) => {
        data = JSON.parse(JSON.stringify(data));
        if (Object.keys(data).length === 0 || data.length === 0) {
          this.resetData();
        } else {
          data.forEach((d) => {
            d.date = timeParseNoNull2(d.date);
            d.purchase_date = timeParseNoNull2(d.purchase_date);
            d.fixation_date = timeParseNoNull2(d.fixation_date);
            d.fixing_date = timeParseNoNull2(d.fixing_date);
            d.p_date = timeParseNoNull2(d.p_date);
            d.sale_date = timeParseNoNull2(d.sale_date);
            d.premium_value_date = timeParseNoNull2(d.premium_value_date);
            d.trade_date = timeParseNoNull2(d.trade_date);
            d.value_date = timeParseNoNull2(d.value_date);
            d.hedged_date = timeParseNoNull2(d.hedged_date);
            d.notional = +d[
              this.userSelections.isCommodity ? "quantity" : "notional"
            ];
            this.analyticsHelpers.existDerivatives(d);
          });
          Object.assign(this.rawData, {
            zoomedData: [],
            mergedExpoHedges: data,
            dDowns: this.analyticsManager.createDDowns([{ details: data }]),
            dataFilters: this.analyticsManager.defineFilters(
              this.customFilters,
            ),
            filteredData: data,
          });
          this.nestAndZoom(data);
        }
      })
      .catch((error) => {
        Object.assign(this.loading, { topographyCalcs: false });
        console.error(error);
      });
  }

  resetData() {
    Object.assign(this.rawData, {
      budget: [],
      dataFilters: this.analyticsManager.defineFilters(this.customFilters),
      dDowns: this.analyticsManager.defineDDowns(),
      expoFuture: [],
      expoPast: [],
      filteredData: [],
      hedgesFuture: [],
      hedgesPast: [],
      historicalData: [],
      historicalOHLC: {},
      mergedExpoHedges: [],
      nestedData: [],
      program: [],
      totals: this.analyticsManager.defineTotals(),
      years: {},
      zoomedData: [],
    });
  }

  /** Consolidate expo and hedges */
  // getPortfolioValuation
  postPortfolioValuation(extraParams = {}) {
    Object.assign(this.valuation, { valuated: true });
    Object.assign(this.valuation, { data: [] });
    Object.assign(this.loading, { accounting: true });

    let endpoint_string = this.userSelections.isCommodity
      ? "commodity_portfolio_valuation"
      : "currency_portfolio_valuation";
    const assets = this.userSelections.isCommodity
      ? "underlyingNums"
      : "assetsNums";

    let params = {
      method: "POST",
      endpoint: endpoint_string,
      content_type: "application/json",
      query_params: {
        assets: { id_assets: this.userSelections[assets] },
        endDate: formatDate_YMD(this.valuation["finalDate"]),
        format: "json",
        initialDate: formatDate_YMD(this.valuation["initialDate"]),
        portfolios: { id_portfolios: this.userSelections["portfolioNums"] },
        ...extraParams,
      },
    };

    return apiService
      .getAll(params)
      .then((data) => {
        data = data.filter(
          (d) => d.name === "Expired hedges" || d.name === "Open positions",
        );
        Object.assign(this.valuation, { data: data });
        this.valuation["data"].forEach((dat) => {
          dat.selected = [];
          dat.details.forEach((d) => {
            d.date = timeParseNoNull2(d.fixing_date);
            d.premium_value_date = timeParseNoNull2(d.premium_value_date);
            d.trade_date = timeParseNoNull2(d.trade_date);
            d.value_date = timeParseNoNull2(d.value_date);
            d.hedged_date = timeParseNoNull2(d.hedged_date);
            d.sale_date = timeParseNoNull2(d.sale_date);
            d.fixing_date = timeParseNoNull2(d.fixing_date);
            d.valuation_date = timeParseNoNull2(d.valuation_date);
            d.futures = this.underlyingObject[d.commodity].commodity_name;
            d.notional = +d[
              this.userSelections.isCommodity ? "quantity" : "notional"
            ];
            d.hedgeNotional = this.analyticsHelpers.calculateHedgeNotional(d); // TODO: puede venir del back
            d.buy_sell = longShortTransform(d.buy_sell.toUpperCase());
            d.derivative = d.derivative.toUpperCase();
            d.cash_flow = 0;
            d.balance = 0;
            d.asian_fixing_start_date = timeParseNoNull2(
              d.asian_fixing_start_date,
            );
            d.dImg = derivativeImage(
              {
                derivative: d.derivative.toLowerCase(),
                buy_sell: d.buy_sell.toLowerCase(),
              },
              this.images["lFwd"],
              this.images["sFwd"],
              this.images["lCall"],
              this.images["sCall"],
              this.images["lPut"],
              this.images["sPut"],
            );
            d.strategy_name = d.strategy_name.toUpperCase();
          });
        });

        Object.assign(this.valuation, {
          dDowns: this.analyticsManager.createDDowns(this.valuation["data"]),
        });
        Object.assign(this.loading, { accounting: false });
      })
      .catch((error) => {
        Object.assign(this.loading, { accounting: false });
        console.error(error);
      });
  }
  /** Retrieves assets for selected portfolios */
  getAssets(portfolios, useAllPortfolios = false) {
    let fieldName = this.userSelections.isCommodity ? "asset" : "underlying";
    const { selectedPortfolios } = this.userSelections;
    let accum = [];
    if (useAllPortfolios) {
      for (let i = 0; i < portfolios.length; i++) {
        for (let j = 0; j < portfolios[i][fieldName].length; j++) {
          accum.push(portfolios[i][fieldName][j]);
        }
      }
    } else if (selectedPortfolios.length > 0) {
      for (let i = 0; i < selectedPortfolios.length; i++) {
        for (let j = 0; j < selectedPortfolios[i][fieldName].length; j++) {
          accum.push(selectedPortfolios[i][fieldName][j]);
        }
      }
    } else if (portfolios.length > 0) {
      accum = portfolios[0][fieldName];
    }
    accum = accum.sort();
    accum = accum.filter(this.processData.onlyUnique);
    return accum;
  }
  /** Creates available portfolios for companies */
  assignPortfoliosByAsset(port) {
    let accum = [];
    let fieldAsset = this.userSelections.isCommodity
      ? "fk_idcommodity"
      : "fk_currency";

    for (let i = 0; i < port.length; i++) {
      const { portfolio, companyName } = port[i];
      if (portfolio[fieldAsset].length !== 0) {
        accum.push({
          pk: portfolio.pk_idportfolio,
          id: i,
          name: portfolio.portfolio_name,
          underlying: portfolio[fieldAsset],
          asset: portfolio["fk_id_end_product"],
          fkCompany: portfolio.fk_idcompany,
          companyName,
        });
      }
    }

    accum = accum.filter(this.processData.onlyUnique);
    accum = sortByKey(accum, "name");
    return accum;
  }
  /** Retrieves user Portfolio for selected companies */
  getPortfolios(comp) {
    let port = [];
    for (let i = 0; i < comp.length; i++) {
      for (let j = 0; j < comp[i].portfolios.length; j++) {
        port.push({
          portfolio: this.companies[comp[i].id].portfolios[j],
          companyName: this.companies[comp[i].id].company_name,
        });
      }
    }
    port = port.filter(this.processData.onlyUnique);
    port = sortByKey(port, "portfolio_name");
    return this.assignPortfoliosByAsset(port);
  }
  /** Retrieves user Company-Portfolio-Underlying Selection from Store or create-saves it */
  getSelectedUnderlying() {
    const { portfolios } = this.userSelections;
    if (!this.userSelections.isCommodity) {
      Object.assign(this.userSelections, {
        underlying: [{ commodity_name: "N.A." }],
      });
      return;
    }
    let i, saveUserPref;
    let assetPK = this.getUnderlying(portfolios);
    let assets = [];

    assetPK.forEach((pk) => {
      assets.push(
        this.underlyingSpecs.filter(
          (d) => d.fk_idproduct.toUpperCase() === pk.toUpperCase(),
        )[0],
      );
    });
    assets = assets.filter((a) => a != null);
    assets = assets.filter(this.processData.onlyUnique);
    assets = sortByKey(assets, "commodity_name");

    Object.assign(this.userSelections, { underlying: assets });
    const underlying = this.userSelections.underlying;

    if (underlying.length === 0) {
      this.userSelections["selectedUnderlying"] = [];
    } else {
      let selAsset,
        selectedAssets = [];
      if (this.isInitial) {
        if ("selectedUnderlying" in this.analyticsPreferences) {
          selAsset = this.analyticsPreferences.selectedUnderlying;
        } else {
          selAsset = [underlying[0]];
        }
      } else {
        selAsset = this.userSelections.selectedUnderlying;
      }

      let idx;
      for (i = 0; i < selAsset.length; i++) {
        idx = underlying.findIndex(
          (d) => d.fk_idproduct === selAsset[i].fk_idproduct,
        );
        if (idx > -1) {
          selectedAssets.push(underlying[idx]);
        }
      }
      Object.assign(this.userSelections, {
        selectedUnderlying: selectedAssets,
      });

      if (this.userSelections.selectedUnderlying.length === 0) {
        Object.assign(this.userSelections, {
          selectedUnderlying: underlying,
        });
      }
      saveUserPref = true;
    }

    Object.assign(this.userSelections, {
      underlyingNums: this.userSelections.selectedUnderlying.map(
        (und) => und.pk_idcommodity,
      ),
    });
    return saveUserPref;
  }

  /** Retrieves underlying for selected portfolios */
  getUnderlying(portfolios) {
    const { selectedPortfolios } = this.userSelections;
    let opt = [];
    if (selectedPortfolios.length > 0) {
      for (let i = 0; i < selectedPortfolios.length; i++) {
        for (let j = 0; j < selectedPortfolios[i]["underlying"].length; j++) {
          opt.push(selectedPortfolios[i]["underlying"][j]);
        }
      }
    } else {
      if (portfolios.length > 0) {
        opt = portfolios[0]["underlying"];
      }
    }
    opt = opt.sort();
    opt = opt.filter(this.processData.onlyUnique);
    return opt;
  }

  /** On user update Selected Asset for portfolios */
  updateSelectedAsset(preferredAssets = null, initial = this.isInitial) {
    this.isInitial = initial;
    const { portfolios } = this.userSelections;
    let i,
      saveUserPref = false;

    // End Product for commodity and currency for currency
    let assetPK = this.getAssets(portfolios);
    let listAssets = [];

    if (this.userSelections.isCommodity) {
      assetPK.forEach((pk) => {
        listAssets.push(
          this.companyParameters.endProducts.filter(
            (d) => d.pk_id_end_product === pk,
          )[0],
        );
      });
    } else {
      listAssets = listAssets.filter(this.processData.onlyUnique);
      assetPK.forEach((curr) => {
        listAssets.push(
          this.underlyingSpecs.filter((d) => d.currency_name === curr)[0],
        );
      });
    }

    listAssets = listAssets.filter((a) => a != null);
    listAssets = listAssets.filter(this.processData.onlyUnique);
    listAssets = sortByKey(
      listAssets,
      this.userSelections.isCommodity ? "product_name" : "currency_name",
    );
    Object.assign(this.userSelections, { assets: listAssets });
    const assets = this.userSelections.assets;

    if (assets.length === 0) {
      this.userSelections["selectedAsset"] = this.userSelections.isCommodity
        ? [{ product_name: "N.A.", fk_idcurrency: "N.A.", fk_idunit: "N.A." }]
        : [
            {
              currency_name: "EURUSD",
              pk_i_idcurrency: 3,
              fk_idcurrency_right: 0,
              fk_idcurrency_left: 0,
            },
          ];
    } else {
      let selAsset,
        selectedAssets = [];
      if (Array.isArray(preferredAssets) && preferredAssets.length > 0) {
        selAsset = preferredAssets;
      } else if (this.isInitial) {
        if (this.userSelections.isCommodity) {
          if ("selected_commodities" in this.analyticsPreferences) {
            selAsset = this.analyticsPreferences.selected_commodities;
          } else {
            selAsset = [assets[0]];
          }
        } else {
          if ("selected_currencies" in this.analyticsPreferences) {
            selAsset = this.analyticsPreferences.selected_currencies;
          } else {
            selAsset = [assets[0]];
          }
        }
      } else {
        selAsset = this.userSelections["selectedAsset"];
      }

      let idx;
      for (i = 0; i < selAsset.length; i++) {
        if (this.userSelections.isCommodity) {
          idx = assets.findIndex(
            (d) => d.pk_id_end_product === selAsset[i].pk_id_end_product,
          );
        } else {
          idx = assets.findIndex(
            (d) => d.pk_i_idcurrency === selAsset[i].pk_i_idcurrency,
          );
        }
        if (idx > -1) {
          selectedAssets.push(assets[idx]);
        }
      }

      if (selectedAssets.length === 0) {
        selectedAssets = [assets[0]];
      }

      Object.assign(this.userSelections, {
        selectedAsset: selectedAssets,
      });
      if (this.userSelections.selectedAsset.length === 0) {
        Object.assign(this.userSelections, {
          selectedUnderlying: [assets[0]],
        });
      }
      saveUserPref = true;
    }

    Object.assign(this.rawData, {
      budget: [],
      existLFwd: false,
      existSFwd: false,
      existLCall: false,
      existSCall: false,
      existLPut: false,
      existSPut: false,
      expoFuture: [],
      expoPast: [],
      hedgesFuture: [],
      hedgesPast: [],
      historicalData: [],
      historicalOHLC: {},
      toBeFetched: true,
      program: [],
    });

    let fieldName = this.userSelections.isCommodity
      ? "pk_id_end_product"
      : "pk_i_idcurrency";
    Object.assign(this.userSelections, {
      assetsNums: this.userSelections.selectedAsset.map((d) => d[fieldName]),
    });

    if (saveUserPref) {
      Object.assign(this.analyticsPreferences, {
        [this.userSelections.isCommodity
          ? "selected_commodities"
          : "selected_currencies"]: this.userSelections.selectedAsset,
      });
    }

    return saveUserPref;
  }

  /** On user update Selected Portfolio-Asset */
  updateSelectedPortf({
    initial,
    preferredPortfolios = null,
    preferredAssets = null,
    analyticsPreferences = this.analyticsPreferences,
  }) {
    this.isInitial = initial;
    const { selectedCompanies } = this.userSelections;
    let i, saveUserPref;

    // portfolios
    Object.assign(this.userSelections, {
      portfolios: this.getPortfolios(selectedCompanies),
    });
    const portfolios = this.userSelections.portfolios;

    if (portfolios.length === 0) {
      this.userSelections["selectedAsset"] = [
        { product_name: "N.A.", fk_idcurrency: "N.A.", fk_idunit: "N.A." },
      ];
    } else {
      let selPort,
        listSelectedPortfolios = [];
      if (
        Array.isArray(preferredPortfolios) &&
        preferredPortfolios.length > 0
      ) {
        selPort = preferredPortfolios;
      } else if (this.isInitial) {
        selPort = analyticsPreferences.selected_portfolio;
      } else {
        selPort = this.userSelections.selectedPortfolios;
      }
      if (selPort === undefined) {
        selPort = [portfolios[0]];
      } else if (selPort[0] === null) {
        selPort = [portfolios[0]];
      }

      let idx;
      // Accumulated portfolios
      for (i = 0; i < selPort.length; i++) {
        idx = portfolios.findIndex(
          (d) => d.pk === selPort[i].pk && d.fkCompany === selPort[i].fkCompany,
        );
        if (idx > -1) {
          listSelectedPortfolios.push(portfolios[idx]);
        }
      }

      Object.assign(this.userSelections, {
        selectedPortfolios: listSelectedPortfolios,
      });

      if (this.userSelections.selectedPortfolios.length === 0) {
        Object.assign(this.userSelections, {
          selectedPortfolios: [portfolios[0]],
        });
        saveUserPref = true;
      }

      saveUserPref = this.updateSelectedAsset(preferredAssets) || saveUserPref;
    }

    Object.assign(this.userSelections, {
      portfolioNums: this.userSelections.selectedPortfolios.map(
        (port) => port.pk,
      ),
    });
    saveUserPref = this.getSelectedUnderlying() || saveUserPref;

    if (saveUserPref) {
      Object.assign(analyticsPreferences, {
        selected_portfolio: this.userSelections.selectedPortfolios,
        [this.userSelections.isCommodity
          ? "selected_commodities"
          : "selected_currencies"]: this.userSelections.selectedAsset,
      });
    }

    return saveUserPref;
  }

  /** On user update Selected Company-Portfolio-Asset **/
  updateSelectedCompanies(
    companies,
    preferredPortfolios = null,
    preferredAssets = null,
  ) {
    Object.assign(this.userSelections, {
      selectedCompanies: companies,
    });
    const saveUserPref = this.updateSelectedPortf({
      initial: false,
      preferredPortfolios,
      preferredAssets,
    });

    // Update analyticsPreferences reference
    if (saveUserPref) {
      Object.assign(this.analyticsPreferences, {
        selected_company: this.userSelections.selectedCompanies,
        selected_portfolio: this.userSelections.selectedPortfolios,
        [this.userSelections.isCommodity
          ? "selected_commodities"
          : "selected_currencies"]: this.userSelections.selectedAsset,
      });
    }

    return saveUserPref;
  }

  /** Retrieves user Company-Portfolio-Asset Selection from Store or create-saves it **/
  initStoreCompPortAsset(analyticsPreferences) {
    const { initialDate, finalDate } = this.valuation;
    let i;
    let saveUserPref = false;
    let selComp = analyticsPreferences.selected_company;
    let selectedCompanies = [];

    if (selComp !== undefined) {
      for (i = 0; i < selComp.length; i++) {
        if (this.companies.find((d) => d === selComp[i]) !== undefined) {
          selectedCompanies.splice(i, 0, selComp[i]);
        }
      }
    }

    if (selectedCompanies.length === 0) {
      selectedCompanies.splice(0, 0, this.companies[0]);
      saveUserPref = true;
    }

    Object.assign(this.userSelections, {
      selectedCompanies,
    });

    let dat;
    if (!initialDate) {
      dat = todayWithoutTime();
      dat.setMonth(dat.getMonth(), 0);
      Object.assign(this.valuation, { initialDate: dat });
    }
    if (!finalDate) {
      dat = todayWithoutTime();
      Object.assign(this.valuation, { finalDate: dat });
    }
    return (
      this.updateSelectedPortf({ initial: true, analyticsPreferences }) ||
      saveUserPref
    );
  }

  /**
   * Retrieves String to show on title for Analytics
   * @param {object} selVar: selected portfolio, country, or etc.
   * @param {string} strField: field name
   * @param {string} defaultField: default field name
   */
  selectionStrFct(selVar, strField, defaultField) {
    let txt = "";
    if (selVar.length === 0) {
      txt = "";
    } else if (selVar.length > 1) {
      txt = selVar.length + defaultField;
    } else {
      selVar.forEach((d) => {
        txt += strField == null ? d : d[strField];
      });
    }
    return txt;
  }

  /**
   * Retrieves user Portfolio for selected companies
   * @param {object} data: data valuated
   */
  thenCandleData(data) {
    let result = { data: [], ohlc: {}, loading: false };
    result["data"] = data;
    let ohlc = JSON.parse(JSON.stringify(data.at(-1)));
    result["ohlc"] = {
      open: +ohlc.open,
      high: +ohlc.high,
      low: +ohlc.low,
      close: +ohlc.close,
    };
    return result;
  }

  getDerivativesValuation(
    _this,
    data,
    fieldName,
    fieldShowLoadingName,
    asset = "currencies",
    spotField = "spot",
    analyticsSelectedAnalysis = "",
    customDate = null,
  ) {
    let analyticsHelpers = this.analyticsHelpers;
    _this[fieldShowLoadingName] = true;
    let analysis =
      analyticsSelectedAnalysis === "valuation"
        ? "custom"
        : _this.pickedAnalysis <= 1 || _this.pickedAnalysis === 4
        ? "now"
        : "future";
    let params = {
      method: "POST",
      endpoint: "currency_derivatives_valuation",
      content_type: "application/json",
      query_params: {
        format: "json",
        spot: _this[spotField],
        derivatives: JSON.stringify(data),
        analysis: analysis,
        asset: asset,
        isCents: _this.isCents,
        customDate: customDate,
      },
    };
    return apiService
      .getAll(params)
      .then((response) => {
        response = JSON.parse(JSON.stringify(response));
        response.forEach(function(d) {
          d.date = timeParse("%Y-%m-%dT%H:%M:%S.%L")(d.date);
          d.p_date = timeParse("%Y-%m-%dT%H:%M:%S.%L")(d.p_date);
          d.premium_value_date = timeParse("%Y-%m-%dT%H:%M:%S.%L")(
            d.premium_value_date,
          );
          d.trade_date = timeParse("%Y-%m-%dT%H:%M:%S.%L")(d.trade_date);
          d.value_date = timeParse("%Y-%m-%dT%H:%M:%S.%L")(d.value_date);
          d.hedged_date = timeParse("%Y-%m-%dT%H:%M:%S.%L")(d.hedged_date);
          d.sale_date = timeParse("%Y-%m-%dT%H:%M:%S.%L")(d.sale_date);
          const isHedgeNew = analyticsHelpers.hedgeConditionNew(
            d.derivative_purpose,
          );
          d.hedgeNotional = analyticsHelpers.calculateHedgeNotional(
            d,
            isHedgeNew,
          );
          d.cash_flow = 0;
          d.balance = 0;
        });
        response.forEach(function(d) {
          delete d.row;
        });
        Object.assign(this.rawData, {
          hedgesFuture: response,
        });
        _this[fieldShowLoadingName] = false;
      })
      .catch(function(error) {
        _this[fieldShowLoadingName] = false;
        console.error(error);
      });
  }

  createAnalyticsVars(
    d,
    includeFutures,
    includeRatio,
    includeBasis,
    includeOtherCosts,
  ) {
    const key = [includeFutures, includeRatio, includeBasis, includeOtherCosts]
      .map((value) => (value ? "1" : "0"))
      .join("");
    let budget = NaN,
      comm = NaN,
      fixed = NaN,
      fixedHedged = NaN,
      hedge = NaN,
      market = NaN,
      net = NaN,
      budgetPerc = NaN,
      commPerc = NaN,
      fixedPerc = NaN,
      fixedHedgedPerc = NaN,
      hedgePerc = NaN,
      marketPerc = NaN,
      netPerc = NaN;
    let calculate = false;

    switch (key) {
      case "1111":
        // Futures + Ratio + Basis + Other Costs
        budget = d["p_budget"];
        comm = d["p_commited"];
        fixed = d["p_fixed"];
        fixedHedged = d["p_fixedHedged"];
        hedge = d["p_hedged"];
        market = d["p_market"];
        net = d["p_net"];
        calculate = true;
        break;

      case "1110":
        // Futures + Ratio + Basis
        budget = d["p_budget"] - d["p_budgetOther"];
        comm = d["p_commited"] - d["p_commitedOther"];
        fixed = d["p_fixed"] - d["p_fixedHedgedOther"];
        fixedHedged = d["p_fixedHedged"] - d["p_fixedHedgedOther"];
        hedge = d["p_hedged"] - d["p_fixedHedgedOther"];
        market = d["p_market"] - d["p_marketOther"];
        net = d["p_net"] - d["p_netOther"];
        calculate = true;
        break;

      case "1101":
        // Futures + Ratio + Other Costs
        budget = d["p_budget"] - d["p_budgetBasis"];
        comm = d["p_commited"] - d["p_commitedBasis"];
        fixed = d["p_fixed"] - d["p_fixedHedgedBasis"];
        fixedHedged = d["p_fixedHedged"] - d["p_fixedHedgedBasis"];
        hedge = d["p_hedged"] - d["p_fixedHedgedBasis"];
        market = d["p_market"] - d["p_marketBasis"];
        net = d["p_net"] - d["p_netBasis"];
        calculate = true;
        break;

      case "1100":
        // Futures + Ratio
        budget = d["p_budgetFut"] * d["p_budgetRatio"];
        comm = d["p_commitedFut"] * d["p_commitedRatio"];
        fixed = d["p_fixedFut"] * d["p_fixedHedgedRatio"];
        fixedHedged = d["p_fixedHedgedFut"] * d["p_fixedHedgedRatio"];
        hedge = d["p_hedgedFut"] * d["p_fixedHedgedRatio"];
        market = d["p_marketFut"] * d["p_marketRatio"];
        net = d["p_netFut"] * d["p_netRatio"];
        calculate = true;
        break;

      case "1011":
        // Futures + Basis + Other Costs
        break;

      case "1010":
        // Futures + Basis
        break;

      case "1001":
        // Futures + Other Costs
        break;

      case "1000":
        // Futures
        budget = d["p_budgetFut"];
        comm = d["p_commitedFut"];
        fixed = d["p_fixedFut"];
        fixedHedged = d["p_fixedHedgedFut"];
        hedge = d["p_hedgedFut"];
        market = d["p_marketFut"];
        net = d["p_netFut"];
        calculate = true;
        break;

      case "0111":
        // Ratio + Basis + Other Costs
        break;

      case "0110":
        // Ratio + Basis
        break;

      case "0101":
        // Ratio + Other Costs
        break;

      case "0100":
        // Ratio
        budget = d["p_budgetRatio"];
        comm = d["p_commitedRatio"];
        fixed = d["p_fixedHedgedRatio"];
        fixedHedged = d["p_fixedHedgedRatio"];
        hedge = d["p_fixedHedgedRatio"];
        market = d["p_marketRatio"];
        net = d["p_netRatio"];
        calculate = true;
        break;

      case "0011":
        // Basis + Other Costs
        budget = d["p_budgetBasis"] + d["p_budgetOther"];
        comm = d["p_commitedBasis"] + d["p_commitedOther"];
        fixed = NaN;
        fixedHedged = NaN;
        hedge = NaN;
        market = d["p_marketBasis"] + d["p_marketOther"];
        net = d["p_netBasis"] + d["p_netOther"];
        calculate = true;
        break;

      case "0010":
        // Basis
        budget = d["p_budgetBasis"];
        comm = d["p_commitedBasis"];
        fixed = d["p_fixedHedgedBasis"];
        fixedHedged = d["p_fixedHedgedBasis"];
        hedge = d["p_fixedHedgedBasis"];
        market = d["p_marketBasis"];
        net = d["p_netBasis"];
        calculate = true;
        break;

      case "0001":
        // Other Costs
        budget = d["p_budgetOther"];
        comm = d["p_commitedOther"];
        fixed = NaN; // d["p_fixedHedgedOther"];
        fixedHedged = d["p_fixedHedgedOther"];
        hedge = NaN; // d["p_fixedHedgedOther"];
        market = d["p_marketOther"];
        net = d["p_netOther"];
        calculate = true;
        break;

      case "0000":
        // None
        break;
    }
    if (calculate) {
      budgetPerc = 1;
      commPerc = comm / budget - 1;
      fixedPerc = fixed / budget - 1;
      fixedHedgedPerc = fixedHedged / budget - 1;
      hedgePerc = hedge / budget - 1;
      marketPerc = market / budget - 1;
      netPerc = net / budget - 1;
    }
    return {
      budget: budget,
      comm: comm,
      fixed: fixed,
      fixedHedged: fixedHedged,
      hedge: hedge,
      market: market,
      net: net,
      budgetPerc: budgetPerc,
      commPerc: commPerc,
      fixedPerc: fixedPerc,
      fixedHedgedPerc: fixedHedgedPerc,
      hedgePerc: hedgePerc,
      marketPerc: marketPerc,
      netPerc: netPerc,
    };
  }
}

/**
 * Validates and initializes analytics preferences similar to Overview
 * @param {object} analyticsPreferences - Raw preferences from store
 * @param {string} assetType - 'commodity' or 'currency'
 * @returns {object} { analytics_preferences, prefsChanged }
 */
export function initAnalyticsPreferences(analyticsPreferences, assetType) {
  const defaults = {
    selected_company: [],
    selected_portfolio: [],
    selected_nav: 0,
    color_theme: true,
    mktRef: "spot",
    intervalGroup: "1M",
    selectedAnalysis: "scenarios",
    selectedAnalysisParent: "scenario",
    selectedTable: "strategies",
  };

  // Add asset-specific defaults
  if (assetType === "commodity") {
    defaults.selected_commodities = [];
    defaults.selectedUnderlying = [];
  } else if (assetType === "currency") {
    defaults.selected_currencies = [];
    defaults.selectedAsset = [];
  }

  let prefs = analyticsPreferences || {};
  let prefsChanged = false;

  // Validate each field
  for (const [key, defaultValue] of Object.entries(defaults)) {
    if (prefs[key] === undefined || typeof prefs[key] !== typeof defaultValue) {
      prefs[key] = defaultValue;
      prefsChanged = true;
    }
  }

  // Validate arrays are actually arrays
  const arrayFields =
    assetType === "commodity"
      ? [
          "selected_company",
          "selected_portfolio",
          "selected_commodities",
          "selectedUnderlying",
        ]
      : [
          "selected_company",
          "selected_portfolio",
          "selected_currencies",
          "selectedAsset",
        ];

  arrayFields.forEach((field) => {
    if (!Array.isArray(prefs[field])) {
      prefs[field] = defaults[field];
      prefsChanged = true;
    }
  });

  return {
    analytics_preferences: prefs,
    prefsChanged,
  };
}

export const translateOnShow = {
  0: "showAve",
  1: "showForecast",
  2: "showFixedFutures",
  3: "showNet",
  4: "showBudget",
  5: "showLFwd",
  6: "showSFwd",
  7: "showLCall",
  8: "showSCall",
  9: "showLPut",
  10: "showSPut",
  11: "showCommited",
  12: "showMarket",
  13: "showAll",
  14: "showProgram",
  15: "showHedgedFutures",
  16: "showFixedHedged",
  includeFutures: "includeFutures",
  includeRatio: "includeRatio",
  includeBasis: "includeBasis",
  includeOtherCosts: "includeOtherCosts",
};

export const infoLegends = [
  {
    label: "Market",
    show: "showMarket",
    classNameIcon: "fa fa-minus",
    colorBdr: "var(--whiteGray)",
    colorBck: "rgba(180, 180, 180, 0.20)",
  },
  {
    label: "Forecast",
    show: "showForecast",
    classNameIcon: "fa fa-minus",
    colorBdr: "var(--expo)",
    colorBck: "var(--symmetry-alpha)",
  },
  {
    label: "Recognized",
    show: "showFixedBasis",
    classNameIcon: "fa fa-minus",
    colorBdr: "rgb(26, 159, 222)",
    colorBck: "var(--hedge-alpha)",
  },
  {
    label: "Hedge",
    show: "showFixedFutures",
    classNameIcon: "fa fa-minus",
    colorBdr: "rgb(9,204,143)",
    colorBck: "var(--fwd-alpha)",
  },
  {
    label: "Net",
    show: "showNet",
    classNameIcon: "fa fa-minus",
    colorBdr: "rgb(255, 224, 0)",
    colorBck: "rgba(255, 224, 0, 0.2)",
  },
  {
    label: "Derivatives",
    show: "showAll",
    classNameIcon: "fa fa-eye",
    colorBdr: "var(--textactive)",
    colorBck: "var(--transparent)",
  },
  {
    label: "Ave",
    show: "showAve",
    classNameIcon: "fa fa-eye",
    colorBdr: "var(--textactive)",
    colorBck: "var(--transparent)",
  },
  {
    label: "+Fwd/Fut",
    show: "showLFwd",
    classNameIcon: "fa fa-plus",
    colorBdr: "var(--fwd)",
    colorBck: "var(--transparent)",
  },
  {
    label: "+Call",
    show: "showLCall",
    classNameIcon: "fa fa-chevron-up",
    colorBdr: "var(--call)",
    colorBck: "var(--transparent)",
  },
  {
    label: "-Call",
    show: "showSCall",
    classNameIcon: "fa fa-chevron-down",
    colorBdr: "var(--dark-call)",
    colorBck: "var(--transparent)",
  },
  {
    label: "-Put",
    show: "showSPut",
    classNameIcon: "fa fa-chevron-down",
    colorBdr: "var(--dark-put)",
    colorBck: "var(--transparent)",
  },
];
